import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { getOrderLookupAuthHeader, getReturnWidgetFetchClient } from "./return-widget-client";
import { LoadOrderWithItemsResponseDto } from "../generated/schema";

type UseSearchPublicOrderParams = {
  providerId: string;
  orderId?: string | null;
  orderName?: string | null;
  secondaryInput?: string | null;
};

// TODO: this should be a POST

export function useSearchPublicOrder() {
  const client = useQueryClient();

  const mutation = useMutation({
    onSuccess: (data) => {
      client.setQueryData(["order", data.orderId], data);
    },

    mutationFn: async ({
      providerId,
      ...params
    }: UseSearchPublicOrderParams) => {
      const _params = Object.fromEntries(
        Object.entries(params).filter(([_, v]) => !!v),
      );

      const response = await getReturnWidgetFetchClient().GET(
        "/public/orders/load-with-items",
        {
          params: {
            query: { providerId, ..._params },
            header: getOrderLookupAuthHeader(),
          },
        },
      );

      if (response.error || response.data.error) {
        throw (
          response.error ?? response.data.error ?? new Error("Unknown error")
        );
      }

      if (!response.data.data) throw new Error("No data returned");

      return response.data.data;
    },
  });

  return mutation;
}

export function usePublicOrderWithItems<T = LoadOrderWithItemsResponseDto>(
  id: string,
  {
    select,
    providerId,
  }: {
    select?: (data: LoadOrderWithItemsResponseDto["data"]) => T;
    providerId?: string;
  } = {},
) {
  return useSuspenseQuery({
    queryKey: ["order", id],
    queryFn: async () => {
      const cached = localStorage.getItem(`${CACHED_ORDER_PREFIX}-${id}`);
      if (cached) return JSON.parse(cached);
      // Cache miss — deep link straight to an order page, or a refresh after
      // storage was cleared. Fetch by orderId (the route param doubles as the
      // search key; the backend resolves it without secondary input) and let
      // fetchOrder repopulate the cache. Without providerId we can't identify
      // the shop, so preserve the old "must be cached" failure.
      if (!providerId) throw new Error("Should be cached (?!)");
      return fetchOrder({ providerId, orderId: id });
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    staleTime: Infinity,
    select: select!,
  });
}

export const CACHED_ORDER_PREFIX = "cached-order";

export async function fetchOrder(params: UseSearchPublicOrderParams) {
  const { providerId, ..._params } = params;

  const filteredParams = Object.fromEntries(
    Object.entries(_params).filter(([_, v]) => !!v),
  );

  const response = await getReturnWidgetFetchClient().GET(
    "/public/orders/load-with-items",
    {
      params: {
        query: { providerId, ...filteredParams },
        header: getOrderLookupAuthHeader(),
      },
    },
  );

  if (response.error || response.data.error) {
    throw response.error ?? response.data.error ?? new Error("Unknown error");
  }

  if (!response.data.data) throw new Error("No data returned");

  localStorage.setItem(
    `${CACHED_ORDER_PREFIX}-${response.data.data.orderId}`,
    JSON.stringify(response.data.data),
  );

  return response.data.data;
}
